import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Order : famille "engagement commercial chiffré".
 *
 * Pièce commerciale à lignes d'articles entre l'organisation et un tiers, du
 * devis à la commande confirmée puis honorée, côté vente comme côté achat
 * (devis d'atelier, ticket de table, commande traiteur, réassort fournisseur,
 * commande e-commerce). Construite sur le patron "famille + type" (cf. asset).
 *
 * Principe : le socle ne connaît pas d'objet commercial concret. Il connaît la
 * famille générique `order` ; le vocabulaire de l'app est porté par `type`
 * (chaîne libre déclarée par l'app : "sales_order", "purchase_order",
 * "quote"...). Le SENS commercial (vente/achat) vit dans `direction`, l'ÉTAT
 * d'avancement (devis, confirmé, livré, annulé) dans `status` : ce sont des
 * états métier, distincts du discriminant `type`.
 *
 * Le tiers concerné (`contactId`) est le cœur de la famille : une commande
 * existe toujours entre l'organisation et un client ou un fournisseur, donc
 * `contactId` est requis (comme eventId/contactId le sont pour registration).
 *
 * Sens des références : `order` porte ses références AMONT / de contexte
 * (contactId, projectId, opportunityId la piste d'origine, priceListId le tarif
 * appliqué). Les pièces AVAL (invoice, payment, shipment, work-order) portent
 * `orderId` de LEUR côté (relations un-à-plusieurs) : on ne les stocke donc pas
 * ici, sinon on ne pourrait en référencer qu'une seule.
 *
 * Accès : la fiche demande un accès par rôle (vente, caisse, facturation) et un
 * portail client borné aux pièces du contact. En v0, la famille reste org-wide
 * (même doctrine que asset) ; l'accès fin (rôles, scoping portail) viendra
 * après. Les montants (totalExclTax / totalInclTax) sont commercialement
 * sensibles et relèveront de ce même contrôle d'accès, pas d'un sous-document.
 */

/** Champs métier de la famille order (hors champs standard du socle). */
export const orderCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "sales_order", "quote"). */
  type: z.string().min(1),
  /** Tiers concerné (client ou fournisseur), cœur de la famille : requis. */
  contactId: z.string().min(1),
  /** Sens commercial, chaîne libre pilotée par l'app (ex. "vente", "achat"). */
  direction: z.string().min(1).optional(),
  /** État d'avancement (ex. "devis", "confirme", "livre", "annule"). */
  status: z.string().min(1).optional(),
  /** Lignes d'articles (structure libre par ligne : produit, quantité, prix...). */
  lines: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Total hors taxes. */
  totalExclTax: z.number().optional(),
  /** Total toutes taxes comprises. */
  totalInclTax: z.number().optional(),
  /** Date de validité (surtout pour un devis), optionnel. */
  validUntil: z.date().optional(),
  /** Chantier / affaire de rattachement, optionnel. */
  projectId: z.string().min(1).optional(),
  /** Piste commerciale d'origine (opportunity), optionnel. */
  opportunityId: z.string().min(1).optional(),
  /** Grille tarifaire appliquée aux lignes (price-list), optionnel. */
  priceListId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille order (champs standard + champs métier). */
export const OrderSchema = z.object({
  ...baseEntityFields,
  ...orderCoreFields,
});

/** Type d'un order. */
export type Order = z.infer<typeof OrderSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const OrderCreateInputSchema = z.object({
  ...orderCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un order. */
export type OrderCreateInput = z.infer<typeof OrderCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const OrderUpdateInputSchema = z
  .object({ ...orderCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un order. */
export type OrderUpdateInput = z.infer<typeof OrderUpdateInputSchema>;
